import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Tag as TagIcon, Download, Loader2 } from 'lucide-react';
import { crmTagsApi, CrmTag } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function TagDetail() {
  const {
    tagName
  } = useParams<{
    tagName: string;
  }>();
  const {
    getSubmissionsByTag
  } = useWorkspace();
  const [tag, setTag] = useState<CrmTag | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchTag = async () => {
      setLoading(true);
      try {
        const tags = await crmTagsApi.list();
        const found = tags.find(t => t.Name === tagName);
        setTag(found || null);
      } catch (err) {
        console.error('Failed to fetch tag:', err);
        toast.error('Failed to load tag details');
      } finally {
        setLoading(false);
      }
    };
    fetchTag();
  }, [tagName]);
  const submissions = getSubmissionsByTag(tagName || '');
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-6727">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-6728" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-6729">
          Loading tag details...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-6730">
      <div className="flex items-center justify-between" data-id="element-6731">
        <div className="flex items-center gap-4" data-id="element-6732">
          <Link to="/tags" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-6733">
            <ArrowLeft className="h-5 w-5" data-id="element-6734" />
          </Link>
          <div data-id="element-6735">
            <div className="flex items-center gap-3" data-id="element-6736">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6737">Tag:</h1>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-6738">
                <TagIcon className="h-3.5 w-3.5" data-id="element-6739" />
                {tag?.Name || tagName}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6740">
              {tag ? `Team: ${tag.Team || '—'}` : 'Tag not found in CRM'}
            </p>
          </div>
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-6741">
          <Download className="h-4 w-4" data-id="element-6742" />
          Export
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6743">
        {submissions.length === 0 ? <div className="p-12 text-center" data-id="element-6744">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6745" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6746">
              No submissions found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6747">
              No submissions have been tagged with "{tagName}".
            </p>
          </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-6748">
            <thead className="bg-gray-50" data-id="element-6749">
              <tr data-id="element-6750">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6751">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6752">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6753">
                  Source Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6754">
                  All Tags
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6755">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-6756">
              {submissions.map(sub => <tr key={sub.id} className="hover:bg-gray-50" data-id="element-6757">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6758">
                    {sub.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-6759">
                    {sub.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6760">
                    {sub.form}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6761">
                    <div className="flex flex-wrap gap-1" data-id="element-6762">
                      {sub.tags?.map(t => <Link key={t} to={`/tags/${t}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${t === tagName ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`} data-id="element-6763">
                          <TagIcon className="h-3 w-3" data-id="element-6764" />
                          {t}
                        </Link>)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6765">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-6766">
                      {sub.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>
    </div>;
}